import {
  COCKPIT_RULES,
  LANDING_LABELS,
  MONTHS_FULL,
  computeAlerts,
  delta,
  describePeriod,
  caTrend,
  dsoSeries,
  elapsedShare,
  eur,
  landing,
  meur,
  monthValues,
  pacing,
  parseMonths,
  pct,
  periodValues,
  ytdMonths,
  type CockpitDataset,
  type CockpitEntity,
  type Delta,
} from '@cockpit/shared';
import {
  AlertList,
  Card,
  CardHeader,
  DeltaBadge,
  EmptyState,
  ErrorState,
  FilterChip,
  KpiStrip,
  KpiTile,
  Meter,
  Notice,
  SectionHeader,
  SourceTag,
  Spinner,
  StatusChip,
  Table,
  Td,
  Th,
  cn,
} from '@cockpit/ui';
import { ArrowRight, Info } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { DsoChart, TrendChart, Waterfall } from '../components/charts.js';
import { PageHeader } from '../components/Layout.js';
import { PeriodPicker } from '../components/PeriodPicker.js';
import { useCockpit } from '../lib/queries.js';

const nf = (d: number) => (v: number) =>
  v.toLocaleString('fr-FR', { minimumFractionDigits: d, maximumFractionDigits: d });
const keur = (eur: number) => `${Math.round(eur / 1000).toLocaleString('fr-FR')} k€`;
const signedM = (v: number) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${meur(Math.abs(v))}`;
const signedPct = (v: number | null) => (v === null ? '' : ` (${v > 0 ? '+' : ''}${Math.round(v)} %)`);
const frDate = (iso: string | null) => (iso ? iso.split('-').reverse().join('/') : '');

function Delta_({ d, label, unit = 'M€' }: { d: Delta; label: string; unit?: 'M€' | 'pt' | 'j' }) {
  const text =
    unit === 'M€'
      ? `${signedM(d.value)}${signedPct(d.pct)}`
      : unit === 'pt'
        ? `${d.value > 0 ? '+' : d.value < 0 ? '−' : ''}${nf(1)(Math.abs(d.value))} pt`
        : `${d.value > 0 ? '+' : d.value < 0 ? '−' : ''}${Math.round(Math.abs(d.value))} j`;
  return (
    <DeltaBadge tone={d.tone} label={label}>
      {text}
    </DeltaBadge>
  );
}

function Metric({
  label,
  value,
  note,
  source,
}: {
  label: ReactNode;
  value: ReactNode;
  note?: ReactNode;
  source?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1 border-t border-line-soft py-3 first:border-t-0 first:pt-0 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
      <div className="flex flex-wrap items-center gap-2 text-[14px] text-ink-2">
        {label}
        {source}
      </div>
      <div className="sm:text-right">
        <div className="tnum font-display text-[17px] font-bold text-ink">{value}</div>
        {note && <div className="mt-0.5 text-[12.5px] text-ink-3">{note}</div>}
      </div>
    </div>
  );
}

function Missing({ children }: { children: ReactNode }) {
  return <p className="py-2 text-[13px] italic text-ink-3">{children}</p>;
}

function EntityPicker({
  d,
  current,
  onPick,
}: {
  d: CockpitDataset;
  current: string;
  onPick: (k: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label="Entité">
      {d.order.map((k) => (
        <FilterChip key={k} active={k === current} onClick={() => onPick(k)}>
          {d.data[k]?.name ?? k}
        </FilterChip>
      ))}
    </div>
  );
}

export function CockpitPage() {
  const q = useCockpit();
  const [params, setParams] = useSearchParams();
  if (q.isLoading) return <Spinner />;
  if (q.error) return <ErrorState>{(q.error as Error).message}</ErrorState>;
  const d = q.data!;
  if (!d.order.length)
    return (
      <EmptyState title="Aucune entité visible">
        Votre compte n’a accès à aucune entité suivie dans le cockpit.
      </EmptyState>
    );
  const ent = params.get('ent') && d.data[params.get('ent')!] ? params.get('ent')! : d.order[0]!;
  const e = d.data[ent]!;
  const years = d.years?.length ? d.years : [d.year];
  const asked = Number(params.get('annee'));
  const year = years.includes(asked) ? asked : d.year;
  const defaultMonths = ytdMonths(e.monthsAvail.length);
  const months = parseMonths(params.get('mois'), defaultMonths);
  const set = (k: string, v: string) => {
    const p = new URLSearchParams(params);
    p.set(k, v);
    setParams(p, { replace: true });
  };
  const setPeriod = (y: number, ms: number[]) => {
    const p = new URLSearchParams(params);
    if (y === d.year) p.delete('annee');
    else p.set('annee', String(y));
    if (ms.join(',') === defaultMonths.join(',')) p.delete('mois');
    else p.set('mois', ms.join(','));
    setParams(p, { replace: true });
  };
  return (
    <EntityView
      d={d}
      e={e}
      entKey={ent}
      year={year}
      months={months}
      years={years}
      onEntity={(k) => set('ent', k)}
      onPeriod={setPeriod}
    />
  );
}

function EntityView({
  d,
  e,
  entKey,
  year,
  months,
  years,
  onEntity,
  onPeriod,
}: {
  d: CockpitDataset;
  e: CockpitEntity;
  entKey: string;
  year: number;
  months: number[];
  years: number[];
  onEntity: (k: string) => void;
  onPeriod: (year: number, months: number[]) => void;
}) {
  const isFinYear = year === d.year;
  const finDate = frDate(d.freshness.fin);
  const opeDate = frDate(d.freshness.ope);
  const lastMonth = e.monthsAvail.length;
  const closedPicked = months.filter((m) => e.monthsAvail.includes(m));
  const refMonth = closedPicked.length ? Math.max(...closedPicked) : lastMonth;
  const v = monthValues(e, refMonth);
  const pv = periodValues(e, d.year, year, months, d.freshness.sync);
  const alerts = isFinYear ? computeAlerts(e, refMonth) : [];
  const trend = caTrend(e, d.year);
  const caSel = trend.lastMonth ? Math.min(Math.max(...months), trend.lastMonth) : refMonth;
  const dash = (x: number | null) => (x === null ? '—' : meur(x));
  const noFin = isFinYear ? 'Non clôturé' : 'Pas de clôture';
  const pace = pacing(e);

  return (
    <div className="flex flex-col gap-10 [&>section]:animate-[rise_320ms_ease-out]">
      <div>
        <PageHeader
          eyebrow="Cockpit mensuel"
          title={e.name}
          sub={`${describePeriod(year, months)} · ${isFinYear ? `comparé au Budget ${d.year} v3` : 'chiffre d’affaires de la base SYNC'}`}
          aside={
            <>
              <SourceTag source="FIN" date={finDate} />
              <SourceTag source="OPE" date={opeDate} />
              <Link
                to="/cockpit/consolide"
                className="ml-2 inline-flex items-center gap-1.5 rounded-sm border border-line bg-surface px-3 py-1.5 text-[13px] font-semibold text-ink no-underline hover:bg-surface-2"
              >
                Vue consolidée <ArrowRight size={16} strokeWidth={1.75} />
              </Link>
            </>
          }
        />
        <div className="flex flex-col gap-2.5 rounded-md border border-line-soft bg-surface px-4 py-3 shadow-od-1">
          <EntityPicker d={d} current={entKey} onPick={onEntity} />
          <PeriodPicker
            years={years}
            year={year}
            finYear={d.year}
            months={months}
            lastClosed={lastMonth}
            onChange={onPeriod}
          />
          <p className="border-t border-line-soft pt-2 text-[12.5px] text-ink-3">
            La période somme les mois choisis et agit sur la performance globale. Tendances et autres blocs
            arrêtés au dernier mois clôturé ({MONTHS_FULL[lastMonth - 1]}) ou à la date de leur source.
          </p>
        </div>
      </div>

      {isFinYear && (
        <section aria-labelledby="prio">
          <SectionHeader id="prio" title="Priorités du mois" />
          <AlertList items={alerts} />
        </section>
      )}

      <section aria-labelledby="perf">
        <SectionHeader
          id="perf"
          title="Performance globale"
          source={
            <>
              <SourceTag source="FIN" date={finDate} />
              {e.caSource && <SourceTag source="SYNC" date={frDate(d.freshness.sync ?? null)} />}
            </>
          }
          sub={
            e.caSource
              ? 'CA : factures et avoirs clients de la base SYNC (montants HT). Marge et REX : clôtures comptables.'
              : undefined
          }
        />
        <KpiStrip>
          <KpiTile
            label="Chiffre d’affaires"
            value={dash(pv.ca)}
            sub={
              pv.ca === null
                ? noFin
                : pv.caBudget === null
                  ? isFinYear
                    ? 'Pas de budget échu'
                    : 'Pas de budget pour cette année'
                  : pv.budgetToDate
                    ? 'Budget des mois échus'
                    : undefined
            }
            delta={
              pv.ca !== null && pv.caBudget !== null ? (
                <Delta_ d={delta(pv.ca, pv.caBudget)} label="vs budget" />
              ) : undefined
            }
          />
          <KpiTile
            label="Marge brute"
            value={dash(pv.marge)}
            sub={pv.marge === null ? noFin : pv.margePct !== null ? `${pct(pv.margePct)} du CA` : undefined}
            delta={
              pv.marge !== null && pv.margeBudget !== null ? (
                <Delta_ d={delta(pv.marge, pv.margeBudget)} label="vs budget" />
              ) : undefined
            }
          />
          <KpiTile
            label="REX complet"
            value={dash(pv.rex)}
            sub={pv.rex === null ? noFin : pv.tauxRex !== null ? `${pct(pv.tauxRex)} du CA` : undefined}
            delta={
              pv.rex !== null && pv.rexBudget !== null ? (
                <Delta_ d={delta(pv.rex, pv.rexBudget)} label="vs budget" />
              ) : undefined
            }
          />
          <KpiTile
            label="Taux de REX"
            value={pv.tauxRex === null ? '—' : pct(pv.tauxRex)}
            sub={pv.tauxRexBudget === null ? undefined : `budget ${pct(pv.tauxRexBudget)}`}
            delta={
              pv.tauxRex !== null && pv.tauxRexBudget !== null ? (
                <Delta_
                  d={delta(pv.tauxRex, pv.tauxRexBudget, { epsilon: 0.05, minRef: 1e9 })}
                  label="vs budget"
                  unit="pt"
                />
              ) : undefined
            }
          />
        </KpiStrip>
        {!pv.closed && (
          <Notice icon={<Info size={18} strokeWidth={1.75} />} className="mt-3 py-2.5">
            {isFinYear
              ? `Marge, REX et budgets sont comptables : ils ne s’affichent que si tous les mois choisis sont clôturés (dernière clôture : ${MONTHS_FULL[lastMonth - 1]}).`
              : 'Pour cette année, seul le chiffre d’affaires est disponible (base SYNC). Les autres blocs du cockpit portent sur l’année du reporting.'}
          </Notice>
        )}
      </section>

      {isFinYear && (
        <>
          <section aria-labelledby="trend">
            <SectionHeader
              id="trend"
              title="Tendance"
              source={
                <>
                  <SourceTag source="FIN" date={finDate} />
                  {trend.from === 'sync' && (
                    <SourceTag source="SYNC" date={frDate(d.freshness.sync ?? null)} />
                  )}
                </>
              }
              sub="Cumul mois par mois, réel contre budget linéarisé. Le CA court sur les 12 mois, jusqu’à la dernière facture."
              actions={
                <div className="flex gap-4 text-[12.5px] text-ink-2">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-0.5 w-4 rounded-pill bg-navy-600" /> Réel {d.year}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-4 border-t-2 border-dashed border-gray-600" /> Budget {d.year} v3
                  </span>
                </div>
              }
            />
            <div className="grid gap-4 md:grid-cols-3">
              {(
                [
                  ['ca', 'Chiffre d’affaires cumulé', v.ca, v.caBudget],
                  ['margeBrute', 'Marge brute cumulée', v.marge, v.margeBudget],
                  ['rex', 'REX complet cumulé', v.rex, v.rexBudget],
                ] as const
              ).map(([k, title, val, bud]) => {
                const wide = k === 'ca' && trend.lastMonth > 0;
                const shown = wide ? (trend.actual[caSel - 1] ?? val) : val;
                const shownBudget = wide
                  ? trend.budget[caSel - 1]! -
                    (trend.budget[caSel - 1]! - (trend.budget[caSel - 2] ?? 0)) *
                      (1 - elapsedShare(d.year, caSel, d.freshness.sync))
                  : bud;
                return (
                  <Card key={k} className="pb-3">
                    <p className="text-[13px] font-semibold text-ink-2">{title}</p>
                    <p className="tnum mt-0.5 font-display text-[20px] font-bold text-ink">{meur(shown)}</p>
                    <p className="text-[12.5px] text-ink-3">budget {meur(shownBudget)}</p>
                    <TrendChart
                      actual={wide ? trend.actual : e.monthly[k].actual}
                      budget={wide ? trend.budget : e.monthly[k].budget}
                      months={wide ? trend.months : e.monthsAvail}
                      selected={wide ? caSel : v.mi + 1}
                      fmt={meur}
                    />
                  </Card>
                );
              })}
            </div>
          </section>

          <section aria-labelledby="landing">
            <SectionHeader
              id="landing"
              title={`Atterrissage ${d.year}`}
              source={<SourceTag source="FIN" date={finDate} />}
              sub={`Projection au rythme actuel (réalisé ÷ ${lastMonth} mois × 12), comparée au budget annuel.`}
            />
            <Card padded={false} className="px-2 py-3">
              <Table minWidth={760}>
                <thead>
                  <tr>
                    <Th>Indicateur</Th>
                    <Th align="right">Réalisé cumulé</Th>
                    <Th className="w-[180px]">Part du budget annuel</Th>
                    <Th align="right">Rythme annualisé</Th>
                    <Th align="right">Budget {d.year} v3</Th>
                    <Th align="right">Écart projeté</Th>
                    <Th>Statut</Th>
                  </tr>
                </thead>
                <tbody>
                  {landing(e).map((r) => (
                    <tr key={r.key}>
                      <Td className="font-semibold">
                        {r.label}
                        {r.lowerIsBetter && (
                          <span className="ml-1.5 text-[12px] font-normal text-ink-3">(coût)</span>
                        )}
                      </Td>
                      <Td align="right">{meur(r.ytd)}</Td>
                      <Td>
                        <div className="flex items-center gap-3">
                          <span className="tnum w-12 shrink-0 whitespace-nowrap text-right text-[13px] text-ink-2">
                            {Math.round(r.pctOfBudget)} %
                          </span>
                          <Meter
                            label={`${r.label} : part du budget annuel`}
                            value={r.pctOfBudget}
                            marker={pace}
                            tone={r.status === 'ok' ? 'navy' : r.status === 'watch' ? 'warn' : 'danger'}
                          />
                        </div>
                      </Td>
                      <Td align="right" className="font-semibold">
                        {meur(r.projection)}
                      </Td>
                      <Td align="right">{meur(r.budget)}</Td>
                      <Td
                        align="right"
                        className={cn(
                          'font-semibold',
                          r.status === 'ok'
                            ? 'text-success-700'
                            : r.status === 'watch'
                              ? 'text-warn-700'
                              : 'text-danger-700',
                        )}
                      >
                        {r.gapPct > 0 ? '+' : ''}
                        {nf(1)(r.gapPct)} %
                      </Td>
                      <Td>
                        <StatusChip status={r.status}>{LANDING_LABELS[r.status]}</StatusChip>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <p className="px-3 pt-3 text-[12px] text-ink-3">
                Le trait vertical des jauges marque le rythme attendu à ce stade ({Math.round(pace)} % de
                l’année).
                {!e.ms && ' Masse salariale à sourcer pour cette entité.'}
              </p>
            </Card>
          </section>

          <section aria-labelledby="metiers">
            <SectionHeader
              id="metiers"
              title="Détail par métier"
              sub="Sources et dates mixtes, indiquées ligne par ligne."
            />
            <div className="grid gap-4 lg:grid-cols-2">
              <ChantierCard e={e} finDate={finDate} pace={pace} />
              <SavCard e={e} finDate={finDate} />
            </div>
          </section>

          <section aria-labelledby="cash">
            <SectionHeader
              id="cash"
              title="Trésorerie"
              source={<SourceTag source="FIN" date={finDate} />}
              sub="Pont de trésorerie reconstruit à partir des bilans comptables, de décembre à la dernière clôture."
            />
            <CashCard e={e} />
          </section>

          <section aria-labelledby="dso">
            <SectionHeader
              id="dso"
              title="DSO, délai moyen de règlement"
              source={<SourceTag source="OPE" date={opeDate} />}
              sub="Balance âgée mensuelle, plus récente que le reste du cockpit."
            />
            <DsoCard e={e} />
          </section>

          <section aria-labelledby="relance">
            <SectionHeader
              id="relance"
              title="Priorités de relance"
              source={<SourceTag source="OPE" date={opeDate} />}
              sub="Les dix comptes où agir en premier, par montant échu."
            />
            <RelanceCard e={e} />
          </section>
        </>
      )}
    </div>
  );
}

function ChantierCard({ e, finDate, pace }: { e: CockpitEntity; finDate: string; pace: number }) {
  const ch = e.chantier;
  const co = e.commercial;
  const done = co ? (co.entrees_ytd / co.objectif_annuel) * 100 : 0;
  const ope = e.facturationOpe;
  return (
    <Card>
      <CardHeader eyebrow="Métier" title="Chantier" />
      <Metric
        label="Chiffre d’affaires"
        source={<SourceTag source="FIN" date={finDate} />}
        value={meur(ch.ca)}
        note={`Budget annuel ${meur(ch.caBudget)} · ${Math.round((ch.ca / ch.caBudget) * 100)} % réalisé`}
      />
      {ope && (
        <Metric
          label="CA opérationnel"
          source={<SourceTag source="OPE" date={ope.date} />}
          value={meur(ope.chantier.n)}
          note={`N-1 ${meur(ope.chantier.n1)} · écart ${signedM(ope.chantier.n - ope.chantier.n1)}${signedPct(((ope.chantier.n - ope.chantier.n1) / ope.chantier.n1) * 100)}`}
        />
      )}
      <Metric
        label="Marge sur coûts directs"
        source={<SourceTag source="FIN" date={finDate} />}
        value={pct(ch.margePct)}
        note={`${meur(ch.marge)} · budget ${pct(ch.margePctBudget)} (${ch.margePct - ch.margePctBudget >= 0 ? '+' : '−'}${nf(1)(Math.abs(ch.margePct - ch.margePctBudget))} pt)`}
      />
      {e.margeOpe && (
        <Metric
          label="Marge opérationnelle"
          source={<SourceTag source="OPE" date={e.margeOpe.date} />}
          value={pct(e.margeOpe.chantierPct, 2)}
          note="Marge des affaires chantier, cumul de l’année (ERP)"
        />
      )}
      <div className="mt-3 border-t border-line-soft pt-4">
        <p className="mb-2 font-display text-[12px] uppercase tracking-[0.18em] text-ink-3">
          Entrées de commandes
        </p>
        {co ? (
          <>
            <div className="mb-2 flex items-baseline justify-between gap-3">
              <span className="text-[13.5px] text-ink-2">Contre l’objectif annuel</span>
              <span className="tnum text-[14px] font-semibold text-ink">
                {meur(co.entrees_ytd / 1e6)} / {meur(co.objectif_annuel / 1e6)}
              </span>
            </div>
            <Meter
              label="Entrées de commandes contre objectif"
              value={done}
              marker={pace}
              tone={done >= pace ? 'success' : 'navy'}
            />
            <p className="mt-2 text-[12.5px] text-ink-3">
              {Math.round(done)} % de l’objectif réalisé, rythme attendu {Math.round(pace)} %.
            </p>
          </>
        ) : (
          <Missing>Données commerciales à sourcer pour cette entité.</Missing>
        )}
        {e.commandesTop && e.commandesTop.top.length > 0 && (
          <div className="mt-4">
            <div className="mb-1.5 flex items-center justify-between text-[12px] text-ink-3">
              <span>Principaux clients (commandes et devis)</span>
              <SourceTag source="OPE" date={e.commandesTop.date} />
            </div>
            <ol className="flex flex-col">
              {e.commandesTop.top.map((c, i) => (
                <li
                  key={c.nom}
                  className="flex items-baseline justify-between gap-3 border-t border-line-soft py-1.5 text-[13px] first:border-t-0"
                >
                  <span className="min-w-0 truncate text-ink-2">
                    <span className="tnum mr-2 text-ink-3">{i + 1}</span>
                    {c.nom}
                  </span>
                  <span className="tnum shrink-0 font-semibold text-ink">{keur(c.montant)}</span>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
    </Card>
  );
}

function SavCard({ e, finDate }: { e: CockpitEntity; finDate: string }) {
  const sv = e.sav;
  const ope = e.facturationOpe;
  const pf = sv.portefeuille;
  return (
    <Card>
      <CardHeader eyebrow="Métier" title="SAV" />
      <Metric
        label="Chiffre d’affaires"
        source={<SourceTag source="FIN" date={finDate} />}
        value={meur(sv.ca)}
        note={`Budget annuel ${meur(sv.caBudget)} · ${Math.round((sv.ca / sv.caBudget) * 100)} % réalisé`}
      />
      {ope && (
        <Metric
          label="CA opérationnel"
          source={<SourceTag source="OPE" date={ope.date} />}
          value={meur(ope.sav.n)}
          note={`N-1 ${meur(ope.sav.n1)} · écart ${signedM(ope.sav.n - ope.sav.n1)}${signedPct(((ope.sav.n - ope.sav.n1) / ope.sav.n1) * 100)}`}
        />
      )}
      <Metric
        label="Marge sur coûts directs"
        source={<SourceTag source="FIN" date={finDate} />}
        value={pct(sv.margePct)}
        note={`${meur(sv.marge)} · budget ${pct(sv.margePctBudget)} (${sv.margePct - sv.margePctBudget >= 0 ? '+' : '−'}${nf(1)(Math.abs(sv.margePct - sv.margePctBudget))} pt)`}
      />
      {!sv.contratMoP && (
        <Missing>Répartition du CA SAV entre contrats, main-d’œuvre et pièces à sourcer.</Missing>
      )}
      <div className="mt-3 border-t border-line-soft pt-4">
        <p className="mb-2 font-display text-[12px] uppercase tracking-[0.18em] text-ink-3">
          Portefeuille de contrats
        </p>
        {pf ? (
          <>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <p className="text-[12px] text-ink-3">Portefeuille</p>
                <p className="tnum font-display text-[17px] font-bold text-ink">{keur(pf.portefAout)}</p>
                <p className="text-[12px] text-ink-3">{keur(pf.portefDec)} en décembre</p>
              </div>
              <div>
                <p className="text-[12px] text-ink-3">Gagnés</p>
                <p className="tnum font-display text-[17px] font-bold text-success-700">+{keur(pf.gagnes)}</p>
              </div>
              <div>
                <p className="text-[12px] text-ink-3">Perdus</p>
                <p className="tnum font-display text-[17px] font-bold text-danger-700">−{keur(pf.perdus)}</p>
              </div>
            </div>
            <p className="mt-3 text-[12.5px] text-ink-3">
              Solde net {pf.solde >= 0 ? '+' : '−'}
              {keur(Math.abs(pf.solde))} hors avenants, avenants {pf.avenants >= 0 ? '+' : '−'}
              {keur(Math.abs(pf.avenants))} · variation {pf.variation >= 0 ? '+' : '−'}
              {keur(Math.abs(pf.variation))} depuis décembre.
            </p>
          </>
        ) : (
          <Missing>Portefeuille de contrats à sourcer pour cette entité.</Missing>
        )}
        <p className="mt-3 text-[12.5px] italic text-ink-3">
          Marge opérationnelle SAV à sourcer : l’export ERP ne couvre que le chantier.
        </p>
      </div>
    </Card>
  );
}

function CashCard({ e }: { e: CockpitEntity }) {
  const cb = e.cashBridge;
  if (!cb)
    return (
      <EmptyState title="Pont de trésorerie à sourcer">Aucun bilan disponible pour cette entité.</EmptyState>
    );
  const net = cb.delta_cash_reel / 1e6;
  const up = net >= 0;
  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div>
          <p className="text-[13px] font-semibold text-ink-2">
            Trésorerie {up ? 'en hausse' : 'en baisse'} depuis décembre
          </p>
          <p
            className={cn(
              'tnum mt-1 font-display text-[32px] font-bold leading-none',
              up ? 'text-success-700' : 'text-danger-700',
            )}
          >
            {signedM(net)}
          </p>
          <p className="mt-2 text-[13px] text-ink-3">
            {meur(cb.dispo_dec2025 / 1e6)} en décembre, {meur(cb.dispo_jul2026 / 1e6)} à la dernière clôture.
          </p>
        </div>
      </div>
      <div className="mt-6">
        <Waterfall
          fmt={(n) => meur(n)}
          steps={[
            { label: 'Disponible décembre', value: cb.dispo_dec2025 / 1e6, total: true },
            { label: 'Autofinancement (CAF)', value: cb.caf_proxy / 1e6 },
            { label: 'Investissement', value: cb.investissement / 1e6 },
            { label: 'Financement', value: cb.financement / 1e6 },
            { label: 'Variation du BFR', value: cb.variation_bfr_residuelle / 1e6 },
            { label: 'Disponible clôture', value: cb.dispo_jul2026 / 1e6, total: true },
          ]}
        />
      </div>
      <p className="mt-4 text-[12px] text-ink-3">
        CAF approchée par le résultat et les amortissements. Variation du BFR obtenue par différence.
      </p>
    </Card>
  );
}

function DsoCard({ e }: { e: CockpitEntity }) {
  if (e.dso.jul == null)
    return <EmptyState title="DSO à sourcer">Aucune balance âgée disponible pour cette entité.</EmptyState>;
  const target = COCKPIT_RULES.dsoTarget;
  const d = delta(e.dso.jul, target, { lowerIsBetter: true, epsilon: 0.5, minRef: 1e9 });
  const series = dsoSeries(e);
  return (
    <Card>
      <div className="grid gap-6 md:grid-cols-[220px_minmax(0,1fr)]">
        <div className="flex flex-col gap-4">
          <div>
            <p className="text-[13px] font-semibold text-ink-2">DSO à la dernière clôture</p>
            <p className="font-display text-[32px] font-bold leading-tight text-ink">
              {Math.round(e.dso.jul)} j
            </p>
            <Delta_ d={d} label={`objectif ${target} j`} unit="j" />
          </div>
          {e.cashBridge && (
            <div>
              <p className="text-[13px] font-semibold text-ink-2">Créances clients au bilan</p>
              <p className="tnum font-display text-[20px] font-bold text-ink">
                {meur(e.cashBridge.creances_clients_jul2026 / 1e6)}
              </p>
            </div>
          )}
        </div>
        <div>
          <DsoChart series={series} target={target} />
          <p className="sr-only">
            {series.map((s) => `${MONTHS_FULL[s.month - 1]} : ${s.dso} jours`).join(', ')}
          </p>
        </div>
      </div>
    </Card>
  );
}

function RelanceCard({ e }: { e: CockpitEntity }) {
  const cr = e.creances;
  if (!cr?.top10.length)
    return (
      <EmptyState title="Balance âgée à sourcer">Aucune créance disponible pour cette entité.</EmptyState>
    );
  const cell = (val: number, tone?: 'warn' | 'danger' | 'strong') => {
    if (!val)
      return (
        <Td align="right" className="text-ink-3">
          —
        </Td>
      );
    return (
      <Td
        align="right"
        className={cn(
          val > 0 && tone === 'warn' && 'font-semibold text-warn-700',
          val > 0 && tone === 'danger' && 'font-semibold text-danger-700',
          val > 0 && tone === 'strong' && 'font-bold text-danger-700',
          val < 0 && 'text-ink-3',
        )}
      >
        {eur(val)}
      </Td>
    );
  };
  return (
    <Card padded={false}>
      <div className="px-5 pt-5">
        <Notice icon={<Info size={18} strokeWidth={1.75} />}>
          <strong className="text-ink">{meur(cr.top10Sum / 1e6)}</strong> sont concentrés sur ces dix comptes,
          soit <strong className="text-ink">{pct(cr.pctOfGross)}</strong> des créances réellement échues (
          {cr.nClients} clients avec un solde, hors avoirs et reclassements).
        </Notice>
      </div>
      <div className="px-2 pb-3 pt-2">
        <Table minWidth={760}>
          <thead>
            <tr>
              <Th>Client</Th>
              <Th align="right">À échoir</Th>
              <Th align="right">0–30 j</Th>
              <Th align="right">30–60 j</Th>
              <Th align="right">60–90 j</Th>
              <Th align="right">90–120 j</Th>
              <Th align="right">Plus de 120 j</Th>
              <Th align="right" className="text-ink">
                Total échu
              </Th>
            </tr>
          </thead>
          <tbody>
            {cr.top10.map((c, i) => (
              <tr key={c.nom}>
                <Td className="max-w-[260px]">
                  <span className="flex items-center gap-2.5">
                    <span
                      className={cn(
                        'tnum inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-sm text-[12px] font-bold',
                        i < 3 ? 'bg-action text-on-action' : 'bg-surface-2 text-ink-2',
                      )}
                    >
                      {i + 1}
                    </span>
                    <span className="truncate font-semibold">{c.nom}</span>
                  </span>
                </Td>
                {cell(c.nonEchu)}
                {cell(c.j30)}
                {cell(c.j60)}
                {cell(c.j90, 'warn')}
                {cell(c.j120, 'danger')}
                {cell(c.jplus, 'strong')}
                <Td align="right" className="font-display font-bold">
                  {keur(c.echu)}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </Card>
  );
}
